/**
 * CameraSwitchPill — Exterior / Interior toggle
 *
 * A two-tab glass pill that switches between the 3D orbit view (Exterior)
 * and the 360° interior panorama view (Interior).
 *
 * Design is consistent with GlassIconButton:
 *   - backdrop-blur, drop-shadow, black glass container
 *   - Active tab: filled bg-black/60 with rounded-full highlight
 *   - Inactive tab: transparent with hover fill
 *
 * Only rendered when features.interiorPano === true AND a panoUrl is set.
 *
 * @see packages/brand-audi/src/components/ui/GlassIconButton.tsx
 * @see docs/ims-brand-frontends/08-ui-screens.md
 */

import { useUiTheme } from '../../hooks/useUiTheme';

export type CameraViewMode = 'exterior' | 'interior';

interface CameraSwitchPillProps {
  mode: CameraViewMode;
  onChange: (mode: CameraViewMode) => void;
  exteriorLabel: string;
  interiorLabel: string;
  className?: string;
}

export function CameraSwitchPill({
  mode,
  onChange,
  exteriorLabel,
  interiorLabel,
  className = '',
}: CameraSwitchPillProps) {
  const theme = useUiTheme();

  return (
    <div
      className={[
        // Glass container
        'flex items-center gap-1',
        'h-10 p-1 rounded-full',
        theme.pill, 'backdrop-blur-[10px]',
        'drop-shadow-[0_4px_10px_rgba(0,0,0,0.4)]',
        'transition-colors duration-700',
        className,
      ].join(' ')}
      role="group"
      aria-label="Switch camera view"
    >
      {/* Exterior tab */}
      <button
        type="button"
        onClick={() => onChange('exterior')}
        aria-pressed={mode === 'exterior'}
        className={[
          'h-8 px-4.5 rounded-full',
          'text-sm font-medium text-white',
          'transition-colors duration-200',
          mode === 'exterior'
            ? `${theme.tabActive} cursor-default`
            : `bg-transparent ${theme.tabInactive} cursor-pointer`,
        ].join(' ')}
      >
        {exteriorLabel}
      </button>

      {/* Interior tab */}
      <button
        type="button"
        onClick={() => onChange('interior')}
        aria-pressed={mode === 'interior'}
        className={[
          'h-8 px-4.5 rounded-full',
          'text-sm font-medium text-white',
          'transition-colors duration-200',
          mode === 'interior'
            ? `${theme.tabActive} cursor-default`
            : `bg-transparent ${theme.tabInactive} cursor-pointer`,
        ].join(' ')}
      >
        {interiorLabel}
      </button>
    </div>
  );
}
